import {
  BookingConfig,
  GridSpacePricingTimePeriod,
  perPricingPeriod,
  SpacePricingRateCategory
} from "@features/leasing/lease-bookings/bookings.model";
import {displayShortDate} from "@ui-kit/framework/util/static/date-format-utils";
import {displayRandAmountWithoutDecimal} from "@ui-kit/framework/util/static/number-format-util";
import {
  displayBoolAsYesNo,
  displayStatus,
  displayString,
  displayUuid
} from "@ui-kit/framework/util/static/string-format-util";
import {
  Boolean,
  Date,
  DisplayFunction,
  DisplayOrder,
  ExportFunction,
  SortDefault,
  String,
  TableDisplayedDefault
} from "@ui-kit/webkit/components/crud-item/crud-item-decorator";
import {AgreementType, LeaseStatus, LeaseSummary, OccupancyType, RentalPricingOption} from "./leasing.model";

type CrudLeaseModel = Pick<LeaseSummary,
  "uuid" |
  "retailerCompanyName" |
  "leaseStartDate" |
  "leaseEndDate" |
  "spaceNames" |
  "totalSize" |
  "rentalType" |
  "totalMonthlyFee" |
  "turnoverPercentageValue" |
  "occupancyType" |
  "agreementType" |
  "leaseStatus" |
  "expiresInDays" |
  "tradingOutsideOfLeasePeriod" |
  "baseBookingConfig"
>;

export class CrudLease implements CrudLeaseModel {

  @DisplayOrder(1)
  @TableDisplayedDefault
  @String("ID")
  @DisplayFunction(displayUuid)
  uuid: string;

  @DisplayOrder(2)
  @TableDisplayedDefault
  @SortDefault("asc")
  @String("Company Name")
  retailerCompanyName: string;

  @DisplayOrder(3)
  @DisplayFunction(displayShortDate)
  @ExportFunction(displayShortDate)
  @TableDisplayedDefault
  @Date("Start Date", "YYYY-MM-DD")
  leaseStartDate: string;

  @DisplayOrder(4)
  @DisplayFunction(displayShortDate)
  @ExportFunction(displayShortDate)
  @TableDisplayedDefault
  @Date("End Date", "YYYY-MM-DD")
  leaseEndDate: string;

  @DisplayOrder(5)
  @TableDisplayedDefault
  @String("Space Name")
  spaceNames: string;

  @DisplayOrder(6)
  @TableDisplayedDefault
  @String("Size")
  spaceSize: string;

  @DisplayOrder(7)
  @DisplayFunction(displayString)
  @TableDisplayedDefault
  @String("Rental Type")
  rentalTypeString!: string;

  @DisplayOrder(8)
  @TableDisplayedDefault
  @String("Monthly Rental Fee")
  monthlyFee: string;

  @DisplayOrder(9)
  @DisplayFunction(displayStatus)
  @TableDisplayedDefault
  @String("Occupancy Type")
  occupancyType!: OccupancyType;

  @DisplayOrder(10)
  @DisplayFunction(displayStatus)
  @TableDisplayedDefault
  @String("Lease Status")
  leaseStatus: LeaseStatus;

  @DisplayOrder(11)
  @TableDisplayedDefault
  @String("Expires in")
  expiresIn: string;

  @DisplayOrder(12)
  @DisplayFunction(displayBoolAsYesNo)
  @TableDisplayedDefault
  @Boolean("Trading Out of Term")
  tradingOutsideOfLeasePeriod: boolean;

  totalMonthlyFee: number;
  turnoverPercentageValue?: number;
  totalSize: number;
  expiresInDays: number;
  agreementType: AgreementType;
  rentalType: RentalPricingOption;
  baseBookingConfig: BookingConfig;

  constructor(obj: any = {}) {
    obj = obj || {};
    this.uuid = obj.uuid;
    this.retailerCompanyName = obj.retailerCompanyName;
    this.leaseStartDate = obj.leaseStartDate;
    this.leaseEndDate = obj.leaseEndDate;
    this.spaceNames = obj.spaceNames;
    this.totalSize = obj.totalSize;
    this.rentalType = obj.rentalType;
    this.totalMonthlyFee = obj.totalMonthlyFee;
    this.occupancyType = obj.occupancyType;
    this.agreementType = obj.agreementType;
    this.leaseStatus = obj.leaseStatus;
    this.turnoverPercentageValue = obj.turnoverPercentageValue;
    this.expiresInDays = obj.expiresInDays;
    this.tradingOutsideOfLeasePeriod = obj.tradingOutsideOfLeasePeriod;
    this.baseBookingConfig = obj.baseBookingConfig;

    this.spaceSize = obj.totalSize + " m²";
    this.monthlyFee = displayRandAmountWithoutDecimal(obj.totalMonthlyFee!) + (!!obj.turnoverPercentageValue ? ' + ' + obj.turnoverPercentageValue + '%' : '');
    this.expiresIn = obj.expiresInDays ? obj.expiresInDays + " days" : '-';
    this.rentalTypeString = this.getRentalType(obj.rentalType);
  }

  getRentalType(typeCode: string | null): string {
    if (typeCode === RentalPricingOption.TURNOVER_PERCENTAGE) return 'Percentage rental';
    if (typeCode === RentalPricingOption.PRICING_MODEL) return 'Fixed per m²';
    if (typeCode === RentalPricingOption.FIXED_PER_SQM_PER_MONTH) return 'Fixed per m²';
    if (typeCode === RentalPricingOption.FIXED_PER_MONTH) return 'Fixed per month';
    if (typeCode === RentalPricingOption.FIXED_FOR_PERIOD) return 'Fixed for period';
    if (typeCode === RentalPricingOption.FIXED_COMPLEX && !!this.baseBookingConfig) {
      const period = Object.values(GridSpacePricingTimePeriod)
        .find(key => perPricingPeriod[key].patternType.includes(this.baseBookingConfig!.patternType));
      const perSqm = this.baseBookingConfig.spacePricingRateCategory! == SpacePricingRateCategory.PER_SQM;
      if (period && perSqm) return `Fixed per ${perPricingPeriod[period!].period} per m²`;
      if (period) return `Fixed per ${perPricingPeriod[period!].period}`;
    }
    return '-';
  }
}

